/* Бизнес-логика: статусы и переходы, расчёт заказа, слоты и конфликты, простой по часам,
   погодные правила и объяснимый скоринг аудитории (Revenue Engine). Чистые функции — без БД и DOM. */
import type {
  AudienceEntry,
  Bay,
  Block,
  Channel,
  Customer,
  CustomerStats,
  DomainCtx,
  Offer,
  OfferDisplayStatus,
  Order,
  OrderItem,
  OrderStatus,
  Payment,
  Role,
  ScoreReason,
  Service,
  Settings,
  Vertical,
  WeatherCond,
  WeatherHour,
  WeatherRuleKey,
} from "./types.js";
import { DAY, HOUR, MIN, ago, at, clamp, now, round, startOfDay, sum, time } from "./utils.js";

export const DEFAULT_SETTINGS: Settings = {
  horizonH: 6,
  loadThreshold: 0.6,
  minLeadMin: 30,
  bufferMin: 10,
  ttlMin: 10,
  maxDiscount: 0.25,
  freqCapDays: 7,
  controlShare: 0.1,
  smsCost: 18,
  acquiring: 0.015,
  depositShare: 0.3,
  holdMin: 10,
  refundWindowH: 2,
};

export const STATUS: Record<OrderStatus, { label: string; tone: string }> = {
  draft: { label: "Черновик", tone: "muted" },
  pending: { label: "Ожидает оплаты", tone: "hold" },
  hold: { label: "Слот удержан", tone: "hold" },
  confirmed: { label: "Подтверждён", tone: "booked" },
  arrived: { label: "Клиент прибыл", tone: "arrived" },
  diagnostics: { label: "Диагностика", tone: "arrived" },
  approval: { label: "Согласование", tone: "hold" },
  parts: { label: "Ждёт запчасти", tone: "hold" },
  in_work: { label: "В работе", tone: "work" },
  ready: { label: "Готов", tone: "ready" },
  done: { label: "Завершён", tone: "done" },
  cancelled: { label: "Отменён", tone: "bad" },
  no_show: { label: "Неявка", tone: "bad" },
};

const FLOW_WASH: Partial<Record<OrderStatus, OrderStatus[]>> = {
  draft: ["confirmed", "pending", "cancelled"],
  pending: ["confirmed", "cancelled"],
  hold: ["confirmed", "cancelled"],
  confirmed: ["arrived", "no_show", "cancelled"],
  arrived: ["in_work", "cancelled"],
  in_work: ["ready"],
  ready: ["done"],
};

const FLOW_STO: Partial<Record<OrderStatus, OrderStatus[]>> = {
  ...FLOW_WASH,
  arrived: ["diagnostics", "in_work", "cancelled"],
  diagnostics: ["approval", "in_work"],
  approval: ["in_work", "cancelled"],
  in_work: ["parts", "ready"],
  parts: ["in_work"],
};

export const ACTION: Partial<Record<OrderStatus, string>> = {
  confirmed: "Подтвердить",
  pending: "Ждать оплату",
  arrived: "Клиент приехал",
  diagnostics: "Начать диагностику",
  approval: "На согласование",
  parts: "Ждём запчасти",
  in_work: "Начать работу",
  ready: "Готово",
  done: "Выдать и закрыть",
  cancelled: "Отменить",
  no_show: "Неявка",
};

export const SOURCE: Record<string, string> = {
  walkin: "Живая очередь",
  phone: "Звонок",
  online: "Онлайн-запись",
  smart: "Смарт-предложение",
};

export const INACTIVE: OrderStatus[] = ["cancelled", "no_show"];
export const IN_PROGRESS: OrderStatus[] = ["in_work", "arrived", "diagnostics", "approval", "parts"];

export const CLASSES = ["Седан / хэтчбек", "Кроссовер", "Внедорожник", "Минивэн"];
export const METHODS = ["Kaspi QR", "Карта", "Наличные"];

export const VERTICAL_LABELS: Record<Vertical, { title: string; bayWord: string; bayWordPlural: string }> = {
  wash: { title: "Автомойка", bayWord: "Бокс", bayWordPlural: "Боксы" },
  sto: { title: "СТО", bayWord: "Подъёмник", bayWordPlural: "Посты" },
};

export const ROLES: Record<Role, string> = {
  owner: "Владелец",
  manager: "Управляющий",
  admin: "Администратор",
  worker: "Исполнитель",
};

export type Permission =
  | "finance"
  | "floorPrice"
  | "maxDiscount"
  | "exportClients"
  | "importClients"
  | "campaigns"
  | "settings"
  | "integrations"
  | "discount"
  | "refund"
  | "users"
  | "booking"
  | "audit";

/** Права по ролям (ТЗ AC 011: администратор не видит финансы и не меняет мин. цену). */
export const PERMISSIONS: Record<Permission, Role[]> = {
  finance: ["owner", "manager"],
  floorPrice: ["owner"],
  maxDiscount: ["owner"],
  exportClients: ["owner"],
  importClients: ["owner", "manager"],
  campaigns: ["owner", "manager"],
  settings: ["owner", "manager"],
  integrations: ["owner"],
  discount: ["owner", "manager", "admin"],
  refund: ["owner", "manager"],
  users: ["owner"],
  booking: ["owner", "manager", "admin"],
  audit: ["owner", "manager"],
};

export function can(role: Role | null | undefined, perm: Permission): boolean {
  return !!role && PERMISSIONS[perm].includes(role);
}

export function flow(vertical: Vertical): Partial<Record<OrderStatus, OrderStatus[]>> {
  return vertical === "sto" ? FLOW_STO : FLOW_WASH;
}

export function nextStatuses(o: Pick<Order, "status">, vertical: Vertical): OrderStatus[] {
  return flow(vertical)[o.status] || [];
}

export function canTransition(from: OrderStatus, to: OrderStatus, vertical: Vertical): boolean {
  return (flow(vertical)[from] || []).includes(to);
}

export function isActive(o: Pick<Order, "status" | "holdUntil">, nowTs = now()): boolean {
  if (INACTIVE.includes(o.status)) return false;
  if (o.status === "hold" && o.holdUntil && o.holdUntil < nowTs) return false;
  return true;
}

export function orderEnd(o: Pick<Order, "start" | "dur">): number {
  return o.start + o.dur * MIN;
}

export function paidAmount(payments: Payment[]): number {
  return sum(
    payments.filter((p) => p.status === "succeeded"),
    (p) => (p.type === "refund" ? -p.amount : p.amount),
  );
}

export interface Totals {
  subtotal: number;
  floor: number;
  cost: number;
  discount: number;
  total: number;
  paid: number;
  due: number;
  acquiring: number;
  margin: number;
}

export function totals(o: Pick<Order, "items" | "discount" | "payments">, cfg?: Pick<Settings, "acquiring">): Totals {
  const subtotal = sum(o.items, (x) => x.price);
  const floor = sum(o.items, (x) => x.floor);
  const cost = sum(o.items, (x) => x.cost);
  const discount = o.discount || 0;
  const total = subtotal - discount;
  const paid = paidAmount(o.payments);
  const cardPaid = sum(
    o.payments.filter((p) => p.method !== "Наличные" && p.type !== "refund" && p.status === "succeeded"),
    (p) => p.amount,
  );
  const acquiring = cardPaid * (cfg ? cfg.acquiring : DEFAULT_SETTINGS.acquiring);
  return {
    subtotal,
    floor,
    cost,
    discount,
    total,
    paid,
    due: Math.max(0, total - paid),
    acquiring,
    margin: total - cost - acquiring,
  };
}

/** Максимально допустимая скидка: итог не может быть ниже суммы минимальных цен. */
export function maxDiscountFor(items: OrderItem[]): number {
  return Math.max(0, sum(items, (x) => x.price) - sum(items, (x) => x.floor));
}

/* ---------- слоты ---------- */

export interface BusyRange {
  start: number;
  end: number;
  order?: Order;
  block?: Block;
}

export function busy(d: Pick<DomainCtx, "orders" | "blocks">, bayId: string, excludeId?: string | null, nowTs = now()): BusyRange[] {
  const list: BusyRange[] = d.orders
    .filter((o) => o.bayId === bayId && o.id !== excludeId && isActive(o, nowTs))
    .map((o) => ({ start: o.start, end: orderEnd(o), order: o }));
  d.blocks
    .filter((b) => b.bayId === bayId)
    .forEach((b) => list.push({ start: b.start, end: b.start + b.dur * MIN, block: b }));
  return list.sort((a, b) => a.start - b.start);
}

export function conflict(
  d: Pick<DomainCtx, "orders" | "blocks">,
  bayId: string,
  start: number,
  dur: number,
  excludeId?: string | null,
  bufferMin = 0,
  nowTs = now(),
): BusyRange | null {
  const buf = bufferMin * MIN;
  const end = start + dur * MIN;
  return busy(d, bayId, excludeId, nowTs).find((x) => x.start < end + buf && x.end + buf > start) || null;
}

export function gaps(d: Pick<DomainCtx, "orders" | "blocks">, bayId: string, from: number, to: number, bufferMin = 0, nowTs = now()) {
  const buf = bufferMin * MIN;
  const out: { start: number; end: number }[] = [];
  let cursor = from;
  for (const b of busy(d, bayId, null, nowTs)) {
    if (b.end + buf <= cursor) continue;
    if (b.start - buf > cursor) out.push({ start: cursor, end: Math.min(b.start - buf, to) });
    cursor = Math.max(cursor, b.end + buf);
    if (cursor >= to) break;
  }
  if (cursor < to) out.push({ start: cursor, end: to });
  return out.filter((g) => g.end - g.start >= 15 * MIN);
}

export function dayBounds(d: Pick<DomainCtx, "location">, dayTs: number) {
  return { open: at(dayTs, d.location.open), close: at(dayTs, d.location.close) };
}

export function baysFor(d: Pick<DomainCtx, "bays" | "services">, items: Pick<OrderItem, "sid">[]): Bay[] {
  const cats = items.map((it) => d.services.find((s) => s.id === it.sid)?.category);
  return d.bays.filter((b) => b.active && cats.every((c) => !c || b.cats.includes(c)));
}

export interface SlotOption {
  bayId: string;
  start: number;
  dur: number;
}

/** Ближайшие свободные слоты под длительность, по совместимым постам (до трёх дней вперёд). */
export function findSlots(d: DomainCtx, items: OrderItem[], from: number, count: number, bufferMin = 0, nowTs = now()): SlotOption[] {
  const dur = sum(items, (x) => x.dur);
  const bays = baysFor(d, items);
  const out: SlotOption[] = [];
  const startDay = startOfDay(from);
  for (let day = 0; day < 3 && out.length < count; day++) {
    const dayTs = startDay + day * DAY;
    const { open, close } = dayBounds(d, dayTs);
    const begin = Math.max(open, round(from + 5 * MIN, 10 * MIN));
    const found: SlotOption[] = [];
    bays.forEach((bay) => {
      gaps(d, bay.id, begin, close, bufferMin, nowTs).forEach((g) => {
        let t = round(g.start + 4 * MIN, 10 * MIN);
        if (t < g.start) t += 10 * MIN;
        for (let k = 0; k < 3 && t + dur * MIN <= g.end; k++, t += 30 * MIN) found.push({ bayId: bay.id, start: t, dur });
      });
    });
    found.sort((a, b) => a.start - b.start);
    const seen = new Set<number>();
    for (const f of found) {
      if (seen.has(f.start)) continue;
      seen.add(f.start);
      out.push(f);
      if (out.length >= count) break;
    }
  }
  return out;
}

export interface HourLoad {
  ts: number;
  load: number;
  freeMin: number;
}

/** Загрузка по часам: занятые пост-минуты / доступные пост-минуты. */
export function loadByHour(d: DomainCtx, fromTs: number, hours: number, nowTs = now()): HourLoad[] {
  const bays = d.bays.filter((b) => b.active);
  const res: HourLoad[] = [];
  if (!bays.length) return res;
  for (let h = 0; h < hours; h++) {
    const hs = round(fromTs, HOUR) + h * HOUR;
    const he = hs + HOUR;
    const { open, close } = dayBounds(d, hs);
    if (hs < open || hs >= close) continue;
    let busyMin = 0;
    bays.forEach((b) => {
      busy(d, b.id, null, nowTs).forEach((x) => {
        const s = Math.max(x.start, hs);
        const e = Math.min(x.end, he);
        if (e > s) busyMin += (e - s) / MIN;
      });
    });
    res.push({ ts: hs, load: busyMin / (bays.length * 60), freeMin: bays.length * 60 - busyMin });
  }
  return res;
}

/* ---------- погода ---------- */

export const COND: Record<WeatherCond, { icon: string; label: string }> = {
  rain: { icon: "rain", label: "Дождь" },
  cloud: { icon: "cloud", label: "Облачно" },
  partly: { icon: "partly", label: "Переменная облачность" },
  sun: { icon: "sun", label: "Ясно" },
  moon: { icon: "moon", label: "Ясно" },
  wind: { icon: "wind", label: "Ветер, пыль" },
  snow: { icon: "snow", label: "Снег" },
};

export const RULES: Record<WeatherRuleKey, { title: string; advice: string; icon: string }> = {
  rain: { title: "Дождь в ближайшие 6 часов", advice: "Продавать салон: чистку, химчистку, обработку кожи. Не давать глубокую скидку на мойку кузова.", icon: "rain" },
  snow: { title: "Снег, реагенты или мороз", advice: "Мойка днища и арок, защитная обработка. Проверьте, что услуга технически доступна.", icon: "snow" },
  dust: { title: "Пыль, ветер, сухо", advice: "Комплексная мойка и антистатик. Учитывайте прогноз осадков.", icon: "wind" },
  after_rain: { title: "Хорошая погода после осадков", advice: "Комплексная мойка. Сначала полная цена или бонус вместо скидки.", icon: "sun" },
  clear: { title: "Ровная погода", advice: "Сильного погодного повода нет — используйте бонус вместо скидки.", icon: "sun" },
  cold: { title: "Похолодание до +3 °C и ниже", advice: "Сезонная смена шин, развал-схождение, проверка АКБ.", icon: "thermo" },
  heat: { title: "Жара выше +28 °C", advice: "Заправка и диагностика кондиционера.", icon: "sun" },
};

export interface WeatherRule {
  key: WeatherRuleKey;
  title: string;
  advice: string;
  icon: string;
  why: string;
}

export function weatherAt(d: Pick<DomainCtx, "weather">, ts: number): WeatherHour | undefined {
  const h = round(ts - 30 * MIN, HOUR);
  return d.weather.find((w) => w.ts === h) || d.weather[0];
}

function rule(key: WeatherRuleKey, why: string): WeatherRule {
  return { key, ...RULES[key], why };
}

export function weatherRule(d: Pick<DomainCtx, "weather" | "vertical">, fromTs: number, horizonH: number): WeatherRule {
  if (!d.weather.length) return rule("clear", "Нет данных прогноза");
  const win = d.weather.filter((w) => w.ts >= fromTs - HOUR && w.ts <= fromTs + horizonH * HOUR);
  if (d.vertical === "sto") {
    const all = d.weather.filter((w) => w.ts >= fromTs);
    if (!all.length) return rule("clear", "Нет данных прогноза");
    const minT = Math.min(...all.map((w) => w.temp));
    if (minT <= 3) return rule("cold", "Минимум " + minT + " °C в прогнозе на 72 часа");
    const maxT = Math.max(...all.map((w) => w.temp));
    if (maxT >= 28) return rule("heat", "До +" + maxT + " °C");
    return rule("clear", "Без резких изменений");
  }
  const rain = win.find((w) => w.cond === "rain");
  if (rain) return rule("rain", "Осадки с " + time(rain.ts) + ", вероятность " + rain.prob + "%");
  if (win.some((w) => w.cond === "snow" || w.temp <= 0)) return rule("snow", "Температура около 0 °C");
  if (win.some((w) => w.cond === "wind")) return rule("dust", "Ветер до " + Math.max(...win.map((w) => w.wind)) + " м/с");
  const before = d.weather.filter((w) => w.ts < fromTs && w.ts > fromTs - 14 * HOUR);
  if (before.some((w) => w.cond === "rain")) return rule("after_rain", "Осадки закончились");
  return rule("clear", "Без осадков");
}

export function servicesForRule(d: Pick<DomainCtx, "services">, ruleKey: string): Service[] {
  return d.services.filter((s) => s.active && s.weather.includes(ruleKey));
}

/* ---------- клиенты ---------- */

export function emptyStats(): CustomerStats {
  return { visits: 0, spent: 0, last: null, first: null, services: [], noShows: 0, upcoming: null, smart: 0, avg: 0, daysSince: null };
}

/** Статистика клиентов по списку заказов (используется на клиенте и в тестах; сервер считает SQL-агрегатами). */
export function customerStats(orders: Order[], customers: Pick<Customer, "id">[], nowTs = now()): Record<string, CustomerStats> {
  const map: Record<string, CustomerStats & { svc: Set<string> }> = {};
  customers.forEach((c) => {
    map[c.id] = { ...emptyStats(), svc: new Set() };
  });
  orders.forEach((o) => {
    const s = map[o.customerId];
    if (!s) return;
    if (o.status === "done") {
      s.visits++;
      s.spent += sum(o.items, (x) => x.price) - (o.discount || 0);
      if (!s.last || o.start > s.last) s.last = o.start;
      if (!s.first || o.start < s.first) s.first = o.start;
      o.items.forEach((it) => s.svc.add(it.sid));
      if (o.source === "smart") s.smart++;
    } else if (o.status === "no_show") {
      s.noShows++;
    } else if (isActive(o, nowTs) && o.start > nowTs - HOUR && o.status !== "ready") {
      if (!s.upcoming || o.start < s.upcoming.start) s.upcoming = { id: o.id, start: o.start, num: o.num };
    }
  });
  const out: Record<string, CustomerStats> = {};
  Object.entries(map).forEach(([id, s]) => {
    const { svc, ...rest } = s;
    out[id] = finalizeStats({ ...rest, services: [...svc] }, nowTs);
  });
  return out;
}

export function finalizeStats(s: CustomerStats, nowTs = now()): CustomerStats {
  return {
    ...s,
    avg: s.visits ? s.spent / s.visits : 0,
    daysSince: s.last ? Math.floor((nowTs - s.last) / DAY) : null,
  };
}

const CHANNEL_LABEL: Record<Channel, string> = { sms: "SMS", push: "push", wa: "WhatsApp" };

/** Объяснимый скоринг по правилам из ТЗ §9.3–9.4: базовые 50 баллов ± факторы, плюс причины исключения. */
export function scoreAudience(
  customers: Customer[],
  stats: Record<string, CustomerStats>,
  cfg: Pick<Settings, "freqCapDays">,
  serviceId: string,
  channel: Channel,
  slotStart: number | null,
  nowTs = now(),
): AudienceEntry[] {
  const slotHour = new Date(slotStart || nowTs).getHours();
  return customers
    .map((c) => {
      const s = stats[c.id] || emptyStats();
      const reasons: ScoreReason[] = [];
      let score = 50;
      let excluded: string | null = null;

      if (!c.consents[channel]) excluded = "Нет согласия на " + CHANNEL_LABEL[channel];
      else if (s.upcoming) excluded = "Уже есть активная запись";
      else if (c.lastOfferAt && nowTs - c.lastOfferAt < cfg.freqCapDays * DAY) excluded = "Получал предложение " + ago(c.lastOfferAt);
      else if (c.fullPriceRegular && c.prefHour != null && Math.abs(c.prefHour - slotHour) <= 1)
        excluded = "Обычно приезжает в это время по полной цене";

      if (s.daysSince != null && s.daysSince > c.interval) {
        score += 20;
        reasons.push({ t: "Не был " + s.daysSince + " дн. при привычных " + c.interval, p: true });
      } else if (s.daysSince != null && s.daysSince < c.interval / 3) {
        score -= 15;
        reasons.push({ t: "Был " + s.daysSince + " дн. назад", p: false });
      }
      if (c.consents.geo && c.distanceKm != null && c.distanceKm <= 5) {
        score += 15;
        reasons.push({ t: "В радиусе " + String(c.distanceKm).replace(".", ",") + " км", p: true });
      }
      if (s.services.includes(serviceId)) {
        score += 12;
        reasons.push({ t: "Покупал эту услугу", p: true });
      }
      if (c.opensMessages) {
        score += 8;
        reasons.push({ t: "Открывает сообщения", p: true });
      }
      if (s.noShows > 0) {
        score -= 12 * s.noShows;
        reasons.push({ t: "Неявки: " + s.noShows, p: false });
      }
      if (s.visits === 0) {
        score -= 10;
        reasons.push({ t: "Нет завершённых визитов", p: false });
      }
      return { customer: c, stats: s, score: clamp(Math.round(score), 0, 100), reasons, excluded };
    })
    .sort((a, b) => (a.excluded ? 1 : 0) - (b.excluded ? 1 : 0) || b.score - a.score);
}

export interface IdleWindow {
  bayId: string;
  bay: string;
  start: number;
  end: number;
  min: number;
}

/** Окна простоя в горизонте анализа (от «сейчас + мин. упреждение» до горизонта или закрытия). */
export function idleWindows(d: DomainCtx, cfg: Pick<Settings, "minLeadMin" | "horizonH" | "bufferMin">, nowTs = now()): IdleWindow[] {
  const from = nowTs + cfg.minLeadMin * MIN;
  const to = nowTs + cfg.horizonH * HOUR;
  const out: IdleWindow[] = [];
  const { close } = dayBounds(d, nowTs);
  d.bays
    .filter((b) => b.active)
    .forEach((bay) => {
      gaps(d, bay.id, round(from + 4 * MIN, 10 * MIN), Math.min(to, close), cfg.bufferMin, nowTs).forEach((g) => {
        const min = (g.end - g.start) / MIN;
        if (min >= 30) out.push({ bayId: bay.id, bay: bay.name, start: g.start, end: g.end, min });
      });
    });
  return out.sort((a, b) => a.start - b.start);
}

/* ---------- смарт-предложения ---------- */

export function offerStatus(o: Pick<Offer, "status" | "funnel" | "limit" | "expiresAt">, nowTs = now()): OfferDisplayStatus {
  if (o.status === "finished" || o.status === "cancelled") return o.status;
  if (o.funnel.paid >= o.limit) return "sold_out";
  if (nowTs > o.expiresAt) return "expired";
  return "active";
}

export const OFFER_STATUS: Record<OfferDisplayStatus, { label: string; tone: string }> = {
  active: { label: "Идёт", tone: "ready" },
  sold_out: { label: "Всё продано", tone: "work" },
  expired: { label: "Истекло", tone: "done" },
  finished: { label: "Завершено", tone: "done" },
  cancelled: { label: "Отменено", tone: "bad" },
};

export interface OfferPricing {
  base: number;
  price: number;
  maxDisc: number;
  disc: number;
  floorHit: boolean;
  note: string | null;
  bonus: Service | null;
  cost: number;
  marginPer: number;
}

/** Цена предложения: не ниже мин. цены и не глубже макс. скидки; в дождь мойка кузова — максимум 10%. */
export function offerPricing(
  svc: Service,
  opts: { rule: string; mode: "discount" | "bonus"; discount: number; bonus: Service | null },
  cfg: Pick<Settings, "maxDiscount" | "acquiring">,
): OfferPricing {
  const base = svc.price;
  let maxDisc = cfg.maxDiscount;
  let note: string | null = null;
  if (opts.rule === "rain" && svc.category === "Мойка") {
    maxDisc = Math.min(maxDisc, 0.1);
    note = "В дождь мойку кузова не продаём с глубокой скидкой — максимум 10%";
  }
  if (opts.rule === "after_rain") note = "После осадков сначала полная цена или бонус вместо скидки";
  const disc = opts.mode === "discount" ? Math.min(opts.discount, maxDisc) : 0;
  let price = round(base * (1 - disc), 500);
  let floorHit = false;
  if (price < svc.floor) {
    price = svc.floor;
    floorHit = true;
  }
  const bonus = opts.mode === "bonus" ? opts.bonus : null;
  const cost = svc.cost + (bonus ? bonus.cost : 0);
  const acquiring = price * cfg.acquiring;
  return { base, price, maxDisc, disc: 1 - price / base, floorHit, note, bonus, cost, marginPer: price - cost - acquiring };
}

/** Размер депозита: доля от итога, округлённая до 500 (не меньше 500), но не больше итога. */
export function depositFor(total: number, cfg: Pick<Settings, "depositShare">): number {
  return Math.min(total, Math.max(500, round(total * cfg.depositShare, 500)));
}

/** Цены строки заказа с учётом класса автомобиля (множитель вертикали). */
export function priceFor(svc: Service, cls: number, classMult: number[]): OrderItem {
  const m = classMult[cls - 1] || 1;
  return {
    sid: svc.id,
    price: round(svc.price * m, 500),
    floor: round(svc.floor * m, 500),
    cost: round(svc.cost * m, 100),
    dur: Math.round(svc.dur * (cls >= 3 ? 1.2 : 1)),
  };
}

export const CLASS_MULT: Record<Vertical, number[]> = {
  wash: [1, 1.2, 1.4, 1.6],
  sto: [1, 1.1, 1.25, 1.4],
};
